import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import type React from 'react';
import { createContext, useContext, useState } from 'react';
import type { CalendarDay, Modifiers } from 'react-day-picker';

import { Calendar } from '~/components/shadcn/custom-calendar.tsx';
import { ScrollArea } from '~/components/shadcn/scroll-area.tsx';
import { Skeleton } from '~/components/shadcn/skeleton.tsx';
import type { Events } from '~/database/supabase/calendar/data.server.ts';
import { getCalendarPageDataQueryOptions } from '~/server/calendar/calendar-page-data-query.ts';
import { cn } from '~/utils/cn.ts';
import CalendarHeader from '~/views/calendar/components/calendar-header.tsx';
import { getCenteredMonth, getDefaultMonth, getMonthPeriod } from '~/views/calendar/utils/calendar.ts';

function shouldFetchData(centerMonth: Date, calendarEvents?: Events) {
	const fetchedDays = Object.keys(calendarEvents || {});

	const { startDate, endDate } = getMonthPeriod(centerMonth, 1);

	if (startDate <= new Date(fetchedDays[0]) || endDate >= new Date(fetchedDays[fetchedDays.length - 1])) {
		return true;
	}

	return false;
}

interface CalendarContainerContextValue {
	readonly calendarEvents?: Events;
	readonly centeredMonth: Date;
	readonly setCenteredMonth: React.Dispatch<React.SetStateAction<Date>>;
	readonly setSelectedDay: React.Dispatch<React.SetStateAction<Date>>;
}

const CalendarContainerContext = createContext<CalendarContainerContextValue | null>(null);

function useCalendarContainerContext(): CalendarContainerContextValue {
	const value = useContext(CalendarContainerContext);

	if (!value) {
		throw new Error('CalendarContainerContext is required');
	}

	return value;
}

function CalendarDaySlot({
	day,
	modifiers,
}: Readonly<{
	readonly day: CalendarDay;
	readonly modifiers: Modifiers;
}>): React.JSX.Element {
	const { calendarEvents, setSelectedDay } = useCalendarContainerContext();

	return (
		<CalendarDayContent
			calendarEvents={calendarEvents}
			day={day}
			modifiers={modifiers}
			setSelectedDay={setSelectedDay}
		/>
	);
}

function CalendarNavSlot(): React.JSX.Element {
	const { centeredMonth, setCenteredMonth } = useCalendarContainerContext();

	return <CalendarHeader currentMonth={centeredMonth} setCurrentMonth={setCenteredMonth} />;
}

export const CalendarContainer: React.FunctionComponent<{
	readonly calendarEvents?: Events;
	readonly numberOfMonths: number;
	readonly selectedDay: Date;
	readonly setCalendarEvents: React.Dispatch<React.SetStateAction<Events | undefined>>;
	readonly setSelectedDay: React.Dispatch<React.SetStateAction<Date>>;
}> = ({ calendarEvents, numberOfMonths, selectedDay, setCalendarEvents, setSelectedDay }) => {
	const [centeredMonth, setCenteredMonth] = useState<Date>(new Date());
	const queryClient = useQueryClient();

	async function handleMonthChange(month: Date) {
		const centerMonth = getCenteredMonth(month, numberOfMonths);
		setCenteredMonth(centerMonth);

		const shouldFetch = shouldFetchData(centerMonth, calendarEvents);

		if (!shouldFetch) {
			return;
		}

		const epoch = String(centerMonth.getTime() / 1000);
		const result = await queryClient.ensureQueryData(getCalendarPageDataQueryOptions({ epoch }));

		setCalendarEvents(result.events);
	}

	return (
		<CalendarContainerContext.Provider value={{ calendarEvents, centeredMonth, setCenteredMonth, setSelectedDay }}>
			<div className={cn('mx-auto flex flex-col items-center', 'w-fit max-w-dvw')}>
				<Calendar
					className={cn('w-fit pt-4 pb-4 md:pb-6 lg:pb-8')}
					classNames={{
						month: cn('flex flex-col'),
						month_caption: cn('flex justify-center pb-1'),
						month_grid: cn(
							'mx-4 h-[310px] border-separate rounded-lg border border-border p-2',
							'bg-card',
							'opacity-80',
						),
						months: cn(
							'mx-auto grid w-fit grid-cols-1 justify-center lg:grid-cols-3',
							numberOfMonths === 3
								? cn(
										'[&>div]:origin-top [&>div]:transition-transform',
										'[&>div:nth-child(2)]:scale-100 md:[&>div:nth-child(2)]:scale-110',
									)
								: '',
						),
						week: cn('mt-2 flex w-full'),
						weekday: cn('w-9 font-normal text-[0.8rem] text-muted-foreground'),
						weekdays: cn('flex w-full'),
					}}
					components={{
						Day: CalendarDaySlot,
						Nav: CalendarNavSlot,
					}}
					mode="single"
					month={getDefaultMonth(centeredMonth, numberOfMonths)}
					numberOfMonths={numberOfMonths}
					onMonthChange={(month) => {
						void handleMonthChange(month);
					}}
					onSelect={setSelectedDay}
					required
					selected={selectedDay}
				/>
			</div>
		</CalendarContainerContext.Provider>
	);
};

export const CalendarPagePending: React.FunctionComponent = () => {
	return (
		<ScrollArea
			classNames={{
				root: 'h-dvh',
				viewport: 'h-dvh',
			}}
		>
			<div className="mx-auto flex w-fit max-w-dvw flex-col items-center gap-6 px-4 pt-4 pb-8">
				<Skeleton className="h-[330px] w-[min(960px,calc(100dvw-2rem))] rounded-lg border border-border bg-card" />
				<Skeleton className="h-[220px] w-[min(960px,calc(100dvw-2rem))] rounded-lg border border-border bg-card" />
			</div>
		</ScrollArea>
	);
};

const CalendarDayContent: React.FunctionComponent<{
	readonly calendarEvents?: Events;
	readonly day: CalendarDay;
	readonly modifiers: Modifiers;
	readonly setSelectedDay: React.Dispatch<React.SetStateAction<Date>>;
}> = ({ calendarEvents, day, modifiers, setSelectedDay }) => {
	const parsedDate = day.date?.toISOString().split('T')[0];
	const dayEvents = calendarEvents?.[parsedDate];

	const isInterested = dayEvents?.some((event) => event.user_interest);
	const isEvents = dayEvents && dayEvents.length > 0;

	return (
		<td>
			<button
				className={cn(
					'relative text-foreground',
					'h-9 w-9 rounded-md p-0 font-normal aria-selected:opacity-100',
					'inline-flex items-center justify-center text-sm',
					'focus:ring-2 focus:ring-tertiary focus:ring-offset-0',
					'hover:bg-accent',
					'focus:bg-accent',
					modifiers.selected ? 'bg-muted' : '',
					modifiers.today ? 'underline' : '',
					modifiers.outside ? 'opacity-50' : '',
				)}
				onKeyDown={(event) => {
					if (event.key === 'Enter') {
						setSelectedDay(day.date);
					}
				}}
				onMouseDown={() => {
					setSelectedDay(day.date);
				}}
				type="button"
			>
				<time dateTime={parsedDate}>
					{format(day.date, 'd')}
					<span
						className={cn(
							'absolute top-0 right-[-3px] z-50 text-[10px] leading-[12px]',
							'h-fit w-fit',
							isInterested ? 'text-primary' : 'text-foreground',
							isEvents ? 'size-4 rounded-full border border-border bg-background' : '',
						)}
					>
						{dayEvents?.length}
					</span>
				</time>
			</button>
		</td>
	);
};
